import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Disc, Plus } from 'lucide-react';
import type { CreateSpoolInput } from '@maker-toolchest/shared';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { SpoolTable, SpoolForm, useSpools, useCreateSpool } from '@/features/spools';

export function SpoolsPage() {
  const [searchParams] = useSearchParams();
  const [formOpen, setFormOpen] = useState(false);

  const initialFilters = useMemo(() => {
    const tenantFilamentId = searchParams.get('tenantFilamentId');
    return tenantFilamentId ? { tenantFilamentId } : undefined;
  }, [searchParams]);

  const { data, isLoading } = useSpools(initialFilters);
  const createMutation = useCreateSpool();

  const hasSpools = isLoading || (data?.data?.length ?? 0) > 0;

  const handleCreateSpool = async (formData: CreateSpoolInput) => {
    try {
      await createMutation.mutateAsync(formData);
      setFormOpen(false);
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Page header - Full width */}
      <div className="flex-shrink-0 p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-foreground">Spools</h1>
            <p className="text-sm text-foreground-muted">
              Track your physical filament spools and their usage
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              leftIcon={<Plus className="h-4 w-4" />}
              onClick={() => setFormOpen(true)}
            >
              Add Spool
            </Button>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading && !hasSpools ? (
          <div className="rounded border border-border bg-background-subtle p-12 text-center">
            <p className="text-foreground-muted">Loading spools...</p>
          </div>
        ) : !hasSpools ? (
          <div className="rounded border border-border bg-background-subtle p-4">
            <EmptyState
              icon={<Disc className="h-10 w-10" />}
              title="No spools yet"
              description="Add your first spool to start tracking filament usage and inventory."
              actionLabel="Add Spool"
              onAction={() => setFormOpen(true)}
            />
          </div>
        ) : (
          <SpoolTable initialFilters={initialFilters} />
        )}
      </div>

      {/* Add Spool Form */}
      <SpoolForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleCreateSpool}
        loading={createMutation.isPending}
      />
    </div>
  );
}
